import { MutationFunction } from '@tanstack/react-query';
import { isMockServices } from 'src/utils';
import { buildApiUrl } from 'src/utils/routing/apiUrlBuilder';
import { getRoutingStrategy } from 'src/utils/routing/routingConfig';
import { getCurrentTenantId } from 'src/stores/tenant';
import { filenameFromContentDisposition } from 'src/utils/fileDownload';
import { getAuthHeaders } from '../utils/enhancedAuthTokenHttp';

export interface DownloadIncomeReportParams {
  loanId: string;
}

export interface IncomeReportFile {
  blob: Blob;
  fileName: string;
}

/**
 * Fetches IncomeXpert's income report for a loan, through our own API.
 *
 * Raw `fetch` rather than the shared http layer: that layer hard-codes
 * `response.json()` and forces `Accept: application/json`, which would throw on
 * a PDF. The URL and tenant routing are built with the same helpers it uses, so
 * path-based and header-based tenancy behave identically here.
 *
 * The API decides the content type -- it passes the vendor's through -- so this
 * asks for anything and names the file from `Content-Disposition`.
 */
const downloadIncomeReport: MutationFunction<
  IncomeReportFile,
  DownloadIncomeReportParams
> = async ({ loanId }) => {
  if (isMockServices()) {
    return {
      blob: new Blob(['%PDF-1.7'], { type: 'application/pdf' }),
      fileName: `Income_Report_${loanId}.pdf`,
    };
  }

  const strategy = getRoutingStrategy();
  const tenantId = getCurrentTenantId();
  const url = buildApiUrl(`/loans/${loanId}/incomes/report`, {
    strategy,
    tenantId,
  });

  const authHeaders = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...authHeaders,
      Accept: '*/*',
      ...(strategy === 'header' && tenantId ? { 'tenant-id': tenantId } : {}),
    },
  });

  if (!response.ok) {
    // The API answers errors in JSON even on this route, and its message says
    // something useful ("This loan has not been sent to IncomeXpert yet").
    const message = await response
      .json()
      .then((body: { message?: string }) => body?.message)
      .catch(() => undefined);
    throw new Error(message || 'Failed to download the income report');
  }

  return {
    blob: await response.blob(),
    fileName:
      filenameFromContentDisposition(
        response.headers.get('content-disposition'),
      ) ?? `Income_Report_${loanId}.pdf`,
  };
};

export default downloadIncomeReport;
